import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import Icon from '../Icon';
import { ModalDelete } from '../ModalDelete';
import { LOGOTYPE_ACCEPT, readLogotypeAsDataUri } from '../../helpers/logotypeFile';
import './styles.scss';

/**
 * Os logotipos guardados de um cliente (BDM-1130).
 *
 * O que o comprador enviou na vitrine chega aqui pela conversão do orçamento;
 * o que o vendedor recebe por fora entra pelo upload. Quando `onSelect` é
 * passado, a lista serve para escolher — é assim que o gerador de amostra
 * deixa de pedir o arquivo de novo.
 */
const CustomerLogotypes = ({
    companyId,
    customerId,
    onSelect,
    onRemoved,
    selectedId = null,
    compact = false
}) => {
    const [logotypes, setLogotypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [arrastando, setArrastando] = useState(false);
    const [toRemove, setToRemove] = useState(null);
    const input = useRef(null);

    const escopo = companyId ? { company_id: companyId } : (customerId ? { customer_id: customerId } : null);

    const load = useCallback(() => {
        if (!escopo) return;

        setLoading(true);

        api.get('/customer-logotype', { params: escopo })
            .then(res => setLogotypes(res.data.resources ?? []))
            .catch(() => setLogotypes([]))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, customerId]);

    useEffect(() => { load(); }, [load]);

    // O gerador precisa dos bytes, e a rota de assets do tenant não responde
    // CORS para o sistema — por isso o Data URI vem do backend.
    const escolher = useCallback(logotype => {
        if (!onSelect) return;

        setBusy(true);

        api.get(`/customer-logotype/${logotype.id}/data`)
            .then(res => onSelect(res.data.resource.data_uri, logotype))
            .catch(() => toast.error('Não foi possível abrir este logotipo.'))
            .finally(() => setBusy(false));
    }, [onSelect]);

    const enviar = file => {
        if (!file || !escopo) return;

        setBusy(true);

        readLogotypeAsDataUri(file)
            .then(dataUri => api.post('/customer-logotype', { ...escopo, file: dataUri, name: file.name }))
            .then(res => {
                const resource = res.data.resource;

                setLogotypes(anteriores => [resource, ...anteriores.filter(l => l.id !== resource.id)]);
                escolher(resource);
            })
            .catch(error => toast.error(error?.response?.data?.message ?? error.message ?? 'Não foi possível enviar o logotipo.'))
            .finally(() => setBusy(false));
    };

    // Com confirmação: a lista é a biblioteca do cliente, e um clique errado
    // apagaria a arte que o vendedor guardou.
    const remover = () => {
        if (!toRemove) return;

        setBusy(true);

        api.delete(`/customer-logotype/${toRemove.id}`, { params: escopo })
            .then(() => {
                setLogotypes(anteriores => anteriores.filter(l => l.id !== toRemove.id));

                if (toRemove.id === selectedId) onRemoved?.(toRemove.id);
            })
            .catch(error => toast.error(error?.response?.data?.message ?? 'Não foi possível remover.'))
            .finally(() => { setBusy(false); setToRemove(null); });
    };

    if (!escopo) {
        return <p className="customer-logotypes-empty">Selecione o cliente do orçamento para ver os logotipos guardados.</p>;
    }

    const abrirUpload = () => input.current?.click();

    const arquivo = (
        <input
            ref={input}
            type="file"
            accept={LOGOTYPE_ACCEPT}
            style={{ display: 'none' }}
            onChange={event => { enviar(event.target.files?.[0]); event.target.value = ''; }}
        />
    );

    const confirmacao = (
        <ModalDelete
            show={!!toRemove}
            close={() => setToRemove(null)}
            action={remover}
            loading={busy}
            text={`Remover o logotipo "${toRemove?.name ?? ''}" deste cliente?`}
        />
    );

    if (compact) {
        return (
            <div className="customer-logotypes compact">
                {confirmacao}

                <div
                    className={arrastando ? 'customer-logotypes-grid arrastando' : 'customer-logotypes-grid'}
                    onDragOver={event => { event.preventDefault(); setArrastando(true); }}
                    onDragLeave={() => setArrastando(false)}
                    onDrop={event => {
                        event.preventDefault();
                        setArrastando(false);
                        enviar(event.dataTransfer.files?.[0]);
                    }}
                >
                    {logotypes.map(logotype => (
                        <div
                            key={logotype.id}
                            className={logotype.id === selectedId ? 'customer-logotype selected' : 'customer-logotype'}
                        >
                            <button
                                type="button"
                                className="customer-logotype-thumb"
                                onClick={() => escolher(logotype)}
                                disabled={busy || !onSelect}
                                title={onSelect ? `Usar "${logotype.name}"` : logotype.name}
                            >
                                <img src={logotype.image_url?.thumbnail} alt={logotype.name} />
                            </button>
                            <button
                                type="button"
                                className="customer-logotype-drop"
                                onClick={() => setToRemove(logotype)}
                                disabled={busy}
                                title={`Remover "${logotype.name}"`}
                                aria-label={`Remover "${logotype.name}"`}
                            >
                                <Icon name="close" size={11} />
                            </button>
                            {logotype.id === selectedId &&
                                <span className="customer-logotype-marca"><Icon name="check" size={11} /></span>
                            }
                        </div>
                    ))}

                    <button type="button" className="customer-logotype-enviar" onClick={abrirUpload} disabled={busy}>
                        <Icon name="upload" size={15} />
                        <span>{busy ? 'Enviando…' : 'Enviar'}</span>
                    </button>
                </div>

                <small className="customer-logotypes-nota">
                    {loading ? 'Carregando os logotipos deste cliente…' : 'PNG, JPG, WebP ou SVG · o que você enviar fica guardado neste cliente'}
                </small>

                {arquivo}
            </div>
        );
    }

    return (
        <div className="customer-logotypes">
            {confirmacao}
            <div className="customer-logotypes-list">
                {logotypes.map(logotype => (
                    <div
                        key={logotype.id}
                        className={logotype.id === selectedId ? 'customer-logotype selected' : 'customer-logotype'}
                    >
                        <button
                            type="button"
                            className="customer-logotype-thumb"
                            onClick={() => escolher(logotype)}
                            disabled={busy || !onSelect}
                            title={onSelect ? 'Usar este logotipo' : logotype.name}
                        >
                            <img src={logotype.image_url?.thumbnail} alt={logotype.name} />
                        </button>
                        <span className="customer-logotype-name">{logotype.name}</span>
                        <button type="button" className="customer-logotype-remove" onClick={() => setToRemove(logotype)} disabled={busy}>
                            Remover
                        </button>
                    </div>
                ))}

                {!loading && logotypes.length === 0 && (
                    <p className="customer-logotypes-empty">Nenhum logotipo guardado para este cliente.</p>
                )}
            </div>

            <button type="button" className="customer-logotypes-upload" onClick={abrirUpload} disabled={busy}>
                {busy ? 'Aguarde…' : 'Enviar logotipo'}
            </button>
            {arquivo}
        </div>
    );
};

export default CustomerLogotypes;
